import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApisDefinitionItem, UpsertApisDefinitionRequest } from '@shared/interfaces/apis-definitions.interface';
import { ColumnFilterComponent } from '@shared/components/column-filter/column-filter.component';
import { ApisDefinitionsService } from '@services/apis-definitions.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
    ApisDefinitionsCreateEditComponent,
    ApisDefinitionsDialogMode,
    ApisDefinitionsFormModel
} from './apis-definitions-create-edit/apis-definitions-create-edit.component';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-apis-definitions-management',
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        ProgressSpinnerModule,
        DialogModule,
        ToastModule,
        ToggleSwitch,
        ColumnFilterComponent,
        ApisDefinitionsCreateEditComponent,
        PageHeaderComponent
    ],
    templateUrl: './apis-definitions-management.component.html',
    styleUrl: './apis-definitions-management.component.scss'
})
export class ApisDefinitionsManagementComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    definitions: ApisDefinitionItem[] = [];
    loading = true;

    displayDialog = false;
    dialogMode: ApisDefinitionsDialogMode = 'add';
    dialogHeader = '';
    selected: ApisDefinitionItem | null = null;
    dialogData: Partial<ApisDefinitionsFormModel> | null = null;

    private apisService = inject(ApisDefinitionsService);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.loadDefinitions();
    }

    loadDefinitions(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.apisService.getApisDefinitions().subscribe({
            next: (data) => {
                this.definitions = data || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: (err) => {
                this.loading = false;
                this.cd.detectChanges();
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: err?.error?.message || 'Failed to load APIS definitions.'
                });
            }
        });
    }

    openAdd(): void {
        this.dialogMode = 'add';
        this.dialogHeader = 'Add New APIS Definition';
        this.selected = null;
        this.dialogData = null;
        this.displayDialog = true;
    }

    onEdit(row: ApisDefinitionItem): void {
        this.dialogMode = 'edit';
        this.dialogHeader = 'Edit APIS Definition';
        this.selected = row;
        this.dialogData = this.mapToDialogData(row);
        this.displayDialog = true;
    }

    onDialogCancel(): void {
        this.displayDialog = false;
    }

    onDialogSave(model: ApisDefinitionsFormModel): void {
        const payload: UpsertApisDefinitionRequest = {
            countryCode: model.countryCode,
            port: model.port,
            providerSupplier: model.providerSupplier ?? '',
            active: model.active,
            providerIds: model.providerIds || []
        };

        if (this.dialogMode === 'add') {
            this.apisService.createApisDefinition(payload).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The APIS definition has been created successfully.'
                    });
                    this.displayDialog = false;
                    this.loadDefinitions();
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: err?.error?.message || 'Failed to create APIS definition.'
                    });
                }
            });
        } else if (this.dialogMode === 'edit' && this.selected?.id) {
            this.apisService.updateApisDefinition(this.selected.id, payload).subscribe({
                next: () => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'The APIS definition has been updated successfully.'
                    });
                    this.displayDialog = false;
                    this.loadDefinitions();
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: err?.error?.message || 'Failed to update APIS definition.'
                    });
                }
            });
        }
    }

    onActiveToggle(row: ApisDefinitionItem): void {
        if (!row?.id) return;

        const prev = !row.active;
        const next = row.active;

        this.apisService.updateApisDefinitionStatus(row.id, next).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Status has been updated successfully.'
                });
            },
            error: () => {
                row.active = prev;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update status.'
                });
            }
        });
    }

    getProvidersDisplay(row: ApisDefinitionItem): string {
        if (!row.providers || row.providers.length === 0) {
            return 'All Providers';
        }
        return row.providers.map((p) => p.explanation).join(', ');
    }

    private mapToDialogData(row: ApisDefinitionItem): Partial<ApisDefinitionsFormModel> {
        return {
            id: row.id,
            countryCode: row.countryCode,
            port: row.port,
            providerIds: row.providerIds || [],
            providerSupplier: row.providerSupplier,
            active: row.active
        };
    }
}
